import type { ReactNode } from "react";
import styled from "styled-components";

import { THEMES } from "../../../constants";

import type { ValidationDueStatus } from "../labAssets.utils";

const LABELS = { overdue: "Overdue", due: "Due" } as const;

// Same shape and palettes as StatusBadge in Files/CaseFileStatusComponent.tsx
// (its "failed" red and "uploading" orange), which is module-private there.
const Badge = styled.span<{ status: "overdue" | "due" }>`
  display: inline-block;
  padding: 2px 4px;
  border-radius: 5px;
  font-size: 10px;
  font-weight: 500;
  line-height: 1.2;
  white-space: nowrap;
  background-color: ${({ status, theme }) =>
    theme.name === THEMES.DARK
      ? status === "overdue"
        ? "rgba(211, 47, 47, 0.2)"
        : "rgba(237, 108, 2, 0.2)"
      : status === "overdue"
      ? "#FCE8E6"
      : "#FFF3E0"};
  color: ${({ status, theme }) =>
    theme.name === THEMES.DARK
      ? status === "overdue"
        ? "#E57373"
        : "#FFB74D"
      : status === "overdue"
      ? "#C5221F"
      : "#E65100"};
  border: 1px solid
    ${({ status, theme }) =>
      theme.name === THEMES.DARK
        ? status === "overdue"
          ? "rgba(211, 47, 47, 0.3)"
          : "rgba(237, 108, 2, 0.3)"
        : status === "overdue"
        ? "rgba(197, 34, 31, 0.2)"
        : "rgba(230, 81, 0, 0.2)"};
`;

/**
 * Validation due/overdue badge (ENG-1680). Renders nothing for null. Pass
 * children (e.g. the due date) to colour that instead of the status label.
 */
export const ValidationDueBadge = ({
  status,
  className,
  children,
}: {
  status: ValidationDueStatus;
  className?: string;
  children?: ReactNode;
}) =>
  status ? (
    <Badge
      status={status}
      className={className}
      data-testid="validation-due-badge"
      data-status={status}
    >
      {children ?? LABELS[status]}
    </Badge>
  ) : null;
